import { Form, Input, Select, Divider } from 'antd';
import * as Component from 'components';
import { ACTION_TITLE, CAST_TYPE_OPTIONS, DEFAULT_CAST_TYPE } from 'constants/constant';
import { observer } from 'mobx-react';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { elevenDbApiService } from 'service/api/elevenDbApiService';
import { peopleApiService } from 'service/api/peopleApiService';
import { PeopleInterface } from 'type/peopleType';
import { CastData, CastFormData, I18nFieldData } from '../../../../shared/types/i18n/i18nTypes';
import ElevenDbLinkingPanel from '../../../../shared/components/eleven-db/ElevenDbLinkingPanel';
import { I18nFormFields } from '../../../../shared/components/i18n/I18nFormFields';
import { convertCastLegacyToI18n } from '../../../../shared/services/i18n/i18nService';
import { slugify } from '../../../../shared/utils/slugify';
import { peopleService } from '../services/peopleService';
import './PeopleEdit.css';

const PeopleEdit: React.FC = () => {
  const [form] = Form.useForm();
  const { id } = useParams();
  const navigate = useNavigate();
  const [people, setPeople] = useState<PeopleInterface | null>(null);
  const [_castData, setCastData] = useState<CastFormData | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [i18nNameValue, setI18nNameValue] = useState<I18nFieldData>({});
  const [elevenDbPersonId, setElevenDbPersonId] = useState<number | null>(null);

  useEffect(() => {
    const fetchPeople = async (): Promise<void> => {
      try {
        // Fetch existing People data
        const people = await peopleApiService.fetchById(Number(id));
        setPeople(people);

        // Convert to Cast data (including i18n fields)
        const castFormData = convertCastLegacyToI18n(people as CastData);
        setCastData(castFormData);

        // Set default values in form
        form.setFieldsValue({
          ...people,
          // i18n fields managed as separate state
        });

        // Set i18n name value
        setI18nNameValue(castFormData.i18n_name || {});

        // Set 11DB person ID if available
        setElevenDbPersonId(people.eleven_db_person_id ?? null);

        setIsLoading(false);
      } catch (error) {
        console.error('Failed to fetch people data:', error);
        setIsLoading(false);
      }
    };

    if (id) {
      fetchPeople();
    }
  }, [form, id]);

  // i18n name field change handler
  const handleI18nNameChange = (value: I18nFieldData) => {
    setI18nNameValue(value);

    // Real-time sync with legacy fields
    if (value.ko) {
      form.setFieldValue('name', value.ko);
    }
    if (value.en) {
      form.setFieldValue('name_en', value.en);
      // Auto-generate slug from English name
      const autoSlug = slugify(value.en);
      form.setFieldValue('slug', autoSlug);
    }
  };

  // 11DB linking handlers
  const handleElevenDbSearch = useCallback(
    (query: string) => elevenDbApiService.searchCastCandidates(Number(id), query),
    [id]
  );

  const handleElevenDbLink = useCallback(
    async (elevenDbId: number) => {
      await elevenDbApiService.linkCast(Number(id), elevenDbId);
      setElevenDbPersonId(elevenDbId);
    },
    [id]
  );

  const handleElevenDbUnlink = useCallback(async () => {
    await elevenDbApiService.unlinkCast(Number(id));
    setElevenDbPersonId(null);
  }, [id]);

  // Sync i18n fields when legacy fields change
  const handleLegacyNameChange = (field: 'name' | 'name_en', value: string) => {
    const language = field === 'name' ? 'ko' : 'en';
    const newI18nValue = {
      ...i18nNameValue,
      [language]: value || undefined,
    };
    setI18nNameValue(newI18nValue);
  };

  return (
    <Component.BasePageComponent>
      {isLoading ? (
        <Component.LoadingSpinnerComponent />
      ) : (
        <div>
          <div className="people-details-page">
            <h1>People: {people?.name_en}</h1>
            <Form form={form} layout="vertical" className="people-details-form">
              {/* 11DB Linking */}
              {id && (
                <ElevenDbLinkingPanel
                  entityType="cast"
                  entityId={Number(id)}
                  entityName={people?.name_en || people?.name || ''}
                  entityNameKo={i18nNameValue?.ko || people?.name || ''}
                  entityNameEn={i18nNameValue?.en || people?.name_en || ''}
                  currentElevenDbId={elevenDbPersonId}
                  onSearch={handleElevenDbSearch}
                  onLink={handleElevenDbLink}
                  onUnlink={handleElevenDbUnlink}
                />
              )}

              {/* Multilingual name input */}
              <I18nFormFields
                fieldName="name"
                required={true}
                fieldType="input"
                value={i18nNameValue}
                onChange={handleI18nNameChange}
                requiredLanguages={['en']}
                placeholder={{
                  ko: 'Enter name in Korean',
                  en: 'Enter name in English',
                  'zh-hans': '请输入中文姓名',
                  'zh-hant': '請輸入中文姓名',
                  vi: 'Nhập tên bằng tiếng Việt',
                }}
              />
              <Form.Item
                name="slug"
                label="slug"
                rules={[{ required: true, message: 'Please enter slug' }]}
                normalize={value => slugify(value || '')}
              >
                <Input />
              </Form.Item>
              <Form.Item
                name="thumbnail"
                label="Thumbnail"
                valuePropName="file"
                getValueFromEvent={e => e?.target?.files?.[0]}
                initialValue={people?.thumbnail}
              >
                <input
                  type="file"
                  accept="image/*"
                  onChange={e => {
                    const file = e.target.files?.[0];
                    if (file) {
                      const imageUrl = URL.createObjectURL(file);
                      setPreviewUrl(imageUrl);
                    }
                    if (e.target.files?.[0]) {
                      form.setFieldsValue({ thumbnail: e.target.files[0] });
                    }
                  }}
                />
                {(previewUrl || people?.thumbnail) && (
                  <img
                    src={previewUrl || people?.thumbnail}
                    alt="thumbnail preview"
                    style={{ maxWidth: '200px', marginTop: '10px' }}
                  />
                )}
              </Form.Item>
              <Form.Item name="type" label="Type" initialValue={people?.type || DEFAULT_CAST_TYPE}>
                <Select options={CAST_TYPE_OPTIONS} placeholder="Select cast type" allowClear />
              </Form.Item>
              <Form.Item name="birth_date" label="birth_date" initialValue={people?.birth_date}>
                <Input type="date" />
              </Form.Item>
              <Form.Item name="nationality" label="nationality" initialValue={people?.nationality}>
                <Input />
              </Form.Item>
            </Form>
          </div>
          <div className="people-detail-update-button">
            <Component.ColorButtonComponent
              text="save"
              onClick={async () => {
                try {
                  // Form validation
                  await form.validateFields();

                  // Combine current form data with i18n data
                  const formData = form.getFieldsValue();
                  const castFormData: CastFormData = {
                    ...formData,
                    i18n_name: i18nNameValue,
                  };

                  // Update through i18n support service
                  await peopleService.updateCastWithI18n(Number(id), castFormData, navigate);
                } catch (error) {
                  console.error('Failed to update people:', error);
                }
              }}
            />
          </div>
        </div>
      )}
    </Component.BasePageComponent>
  );
};

export default observer(PeopleEdit);
